import { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';
import { CoachConversationSummary } from '../../api/coachAPI';
import { CoachHistoryStatus } from '../../common/customHooks/useAgentStream';
import { ChatHistoryList } from './ChatHistoryList';

interface ChatHistoryDialogProps {
  conversations: CoachConversationSummary[];
  historyStatus: CoachHistoryStatus;
  activeConversationId: string;
  isNewChat: boolean;
  onNewChat: () => void;
  onSelectConversation: (conversationId: string) => void;
  onRetry: () => void;
  onRenameConversation: (conversationId: string, title: string) => Promise<void>;
  onDeleteConversation: (conversationId: string) => Promise<void>;
}

/**
 * Chat History on small screens: a full-width button above the chat that opens
 * the member's past conversations in a dialog (SN-988).
 *
 * The panel is not stacked above the chat here because on a phone a list of any
 * length pushes the chat itself off the first screenful. Picking a chat closes
 * the dialog, so the member lands straight back on the conversation they chose.
 *
 * The design does not draw New Chat inside the dialog, but it is the only way to
 * start a chat once the panel is behind one, so it is kept above the list as it
 * is on desktop.
 */
export function ChatHistoryDialog({
  conversations,
  historyStatus,
  activeConversationId,
  isNewChat,
  onNewChat,
  onSelectConversation,
  onRetry,
  onRenameConversation,
  onDeleteConversation,
}: ChatHistoryDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // MUI does not wire DialogTitle up to the dialog itself, so without this the
  // heading is read as ordinary text and the dialog announces as unlabelled.
  const headingId = 'coach-history-dialog-title';

  const close = () => setIsOpen(false);

  const selectAndClose = (conversationId: string) => {
    onSelectConversation(conversationId);
    close();
  };

  const startNewChatAndClose = () => {
    onNewChat();
    close();
  };

  return (
    <>
      <OptimusButton
        data-testid={TestIds.coach.history.openDialogButton}
        customVariant="secondaryGray"
        buttonText={`${t('coach.history.title')}`}
        onClick={() => setIsOpen(true)}
      />

      <Dialog
        open={isOpen}
        onClose={close}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby={headingId}
        data-testid={TestIds.coach.history.dialog}
        PaperProps={{
          sx: {
            borderRadius: theme.spacing(1),
            margin: theme.spacing(2),
            // Leaves the dialog room to breathe on a phone while keeping the
            // overflow inside the list, the way the panel behaves on desktop.
            maxHeight: `calc(100% - ${theme.spacing(8)})`,
          },
        }}
      >
        <DialogTitle sx={{ padding: theme.spacing(3, 3, 2, 3) }}>
          <Typography
            id={headingId}
            variant="headingMd"
            data-testid={TestIds.coach.history.heading}
            sx={{ color: theme.palette.grey[900] }}
          >
            {t('coach.history.dialogTitle')}
          </Typography>
        </DialogTitle>

        <Divider />

        {/* Outside the scrolling content below, so it stays put while a long list
            scrolls past it - the same fixed heading and New Chat the panel has on
            desktop (SN-988 mockup #3). Available the whole time, loading list or
            not - see ChatHistory. */}
        <Box sx={{ padding: theme.spacing(2), flexShrink: 0 }}>
          <OptimusButton
            data-testid={TestIds.coach.history.newChatButton}
            customVariant="secondaryGray"
            buttonText={`${t('coach.history.newChat')}`}
            leadingIcon={<ChatIcon fontSize="small" />}
            onClick={startNewChatAndClose}
            disabled={isNewChat}
            size="s"
            sx={{
              color: theme.palette.brand.primary,
              borderColor: theme.palette.brand.primary,
            }}
          />
        </Box>

        {/* Not `dividers`: the rules are drawn around the fixed area above and the
            footer below instead, so the list is not fenced off by a second rule
            immediately under New Chat. */}
        <DialogContent
          data-testid={TestIds.coach.history.scrollArea}
          sx={{ padding: 0 }}
        >
          <ChatHistoryList
            conversations={conversations}
            historyStatus={historyStatus}
            activeConversationId={activeConversationId}
            isNewChat={isNewChat}
            onSelectConversation={selectAndClose}
            onRetry={onRetry}
            onRenameConversation={onRenameConversation}
            onDeleteConversation={onDeleteConversation}
            markCurrentChat
          />
        </DialogContent>

        <Divider />

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: theme.spacing(2, 3),
          flexShrink: 0,
        }}>
          <OptimusButton
            data-testid={TestIds.coach.history.closeDialogButton}
            buttonText={`${t('coach.history.close')}`}
            onClick={close}
            fullWidth={false}
            size="s"
          />
        </Box>
      </Dialog>
    </>
  );
}
